/**
 * @file Frontend utility — formatUnitSymbol.
 * Display-only formatting for unit symbols (subscript/superscript via Unicode).
 * Stored values in the DB/API stay plain (e.g. kg/m3, kg CO2e); call this when rendering.
 */

const FORMATTED_MARKERS = /[²³⁰¹⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉°]/;

function hasFormattedMarkers(text: string): boolean {
  return FORMATTED_MARKERS.test(text);
}

/** Plain unit text → Unicode subscript/superscript for UI (selects, readonly, tables). */
export function formatUnitSymbol(raw: string | null | undefined): string {
  if (raw == null) return "";
  let s = String(raw).trim();
  if (!s) return "";

  if (hasFormattedMarkers(s)) return s;

  s = s.replace(/\bdeg\s+C\b/gi, "°C");
  s = s.replace(/\bdeg\s+F\b/gi, "°F");
  s = s.replace(/\bdegC\b/gi, "°C");
  s = s.replace(/\bdegF\b/gi, "°F");

  s = s.replace(/CO2eq/gi, "CO₂eq");
  s = s.replace(/CO2e(?![a-zA-Z])/gi, "CO₂e");
  s = s.replace(/CO2(?![a-zA-Z])/gi, "CO₂");

  s = s.replace(/kg\/m3\b/gi, "kg/m³");
  s = s.replace(/g\/cm3\b/gi, "g/cm³");
  s = s.replace(/W\/m2\.K\b/gi, "W/m²·K");
  s = s.replace(/W\/m2K\b/gi, "W/m²K");
  s = s.replace(/N\/m2\b/gi, "N/m²");
  s = s.replace(/\/m3\b/gi, "/m³");
  s = s.replace(/\/m2\b/gi, "/m²");
  s = s.replace(/\/cm3\b/gi, "/cm³");
  s = s.replace(/\/cm2\b/gi, "/cm²");
  s = s.replace(/\/mm3\b/gi, "/mm³");
  s = s.replace(/\/mm2\b/gi, "/mm²");
  s = s.replace(/\/km2\b/gi, "/km²");

  s = s.replace(/\bm3\b/gi, "m³");
  s = s.replace(/\bm2\b/gi, "m²");
  s = s.replace(/\bcm3\b/gi, "cm³");
  s = s.replace(/\bcm2\b/gi, "cm²");
  s = s.replace(/\bmm3\b/gi, "mm³");
  s = s.replace(/\bmm2\b/gi, "mm²");
  s = s.replace(/\bkm2\b/gi, "km²");

  return s;
}

type UnitLike = {
  symbol?: string | null;
  unitSymbol?: string | null;
  name?: string | null;
  displayName?: string | null;
  id?: number | string | null;
};

/** Label for a unit dropdown option (display only; option value stays id or plain stored text). */
export function formatUnitOptionLabel(unit: UnitLike): string {
  const sym = unit.symbol ?? unit.unitSymbol;
  const name = unit.name ?? unit.displayName;
  const raw =
    (typeof sym === "string" && sym.trim()) ||
    (typeof name === "string" && name.trim()) ||
    (unit.id != null ? String(unit.id) : "");
  return formatUnitSymbol(raw);
}

/** Format compound unit strings such as `kg CO2e/kg` or `kWh/m3`. */
export function formatCompoundUnitSymbol(raw: string | null | undefined): string {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (s.includes("/")) {
    const [num, ...rest] = s.split("/");
    const den = rest.join("/");
    return `${formatUnitSymbol(num)}/${formatUnitSymbol(den)}`;
  }
  return formatUnitSymbol(s);
}
